import type { ReactNode } from 'react'
import { Platform, View } from 'react-native'

import { Button } from '@/src/elements/button/default'
import { Icon } from '@/src/elements/media/Icon'
import { Typography } from '@/src/elements/text/Typography'
import { ICON } from '@/src/general/enums/icons'
import { useTranslate } from '@/src/lang/useTranslate'

type TScreenErrorFallbackProps = {
  /**
   * Optional callback to reset the error boundary and retry loading the tab
   */
  resetError?: VoidFunction
  /**
   * Optional test ID for testing purposes
   */
  testID?: string
}

/**
 * Fallback UI component displayed when a tab encounters an error.
 * Shows a user-friendly generic error message with a retry button.
 *
 * On web platforms, the retry button reloads the entire page.
 * On native platforms (iOS/Android), it calls the resetError callback if provided.
 */
export const ScreenErrorFallback = ({ resetError, testID }: TScreenErrorFallbackProps): ReactNode => {
  const { t } = useTranslate()

  const handleRetry = () => {
    if (Platform.OS === 'web') {
      window.location.reload()
    } else {
      resetError?.()
    }
  }

  return (
    <View className="bg-background flex-1 items-center justify-center px-6" testID={testID}>
      <Icon name={ICON.ALERT_TRIANGLE} size="xl" className="mb-4 text-danger-icon-contrast-lower" />

      <Typography size="xl" colors="danger" className="mb-2 text-center" black testID={`${testID}-title`}>
        {t('common.error.unknown')}
      </Typography>

      <Typography size="md" colors="default" className="mb-6 text-center" testID={`${testID}-description`}>
        {t('tab.error.generic-description')}
      </Typography>

      <Button variant="solid" colors="primary" onPress={handleRetry} testID={`${testID}-retry-button`}>
        {t('tab.error.try-again')}
      </Button>
    </View>
  )
}
